import { styled } from "@mui/material/styles";
import { AcquireBookDataGrid } from "@/components/DataGrid";
import {
  type GridColDef,
  type GridValueFormatterParams,
  type GridRowParams,
} from "@mui/x-data-grid-pro";
import { type UserSearchHistoryTitleResponseType } from "../types/";
import { format } from "date-fns";
import ja from "date-fns/locale/ja";
import { getPrefectureNamesByIds } from "@/utils/prefecture";

interface SearchHistoryGridProps {
  loading: boolean;
  rows: UserSearchHistoryTitleResponseType[];
  onRowClick: (row: UserSearchHistoryTitleResponseType) => void;
  pageSize: number;
  rowsPerPageOptions: number[];
  rowCount: number;
  onPageSizeChange?: (newPageSize: number) => void;
  page?: number;
  onPageChange?: (newPage: number) => void;
}

const StyledDataGrid = styled(AcquireBookDataGrid)(({ theme }) => ({
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
    outline: "none !important",
  },

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
    {
      outline: "none !important",
    },

  // テーブルヘッダーの背景色を設定
  "& .MuiDataGrid-columnHeaders": {
    lineHeight: "1.5em !important",
  },

  // テーブルタイトルを太字に
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 700,
  },

  // データがない場合のメッセージを中央に表示
  "& .MuiDataGrid-virtualScroller .MuiDataGrid-overlay": {
    position: "static",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    background: "white",
    color: "rgba(0, 0, 0, 0.87)",
    minHeight: "150px", // 空のグリッドの高さを確保
  },

  // 行の高さを固定
  "& .MuiDataGrid-row": {
    minHeight: "48px !important",
    cursor: "pointer",
  },

  // セルの中央寄せと長いテキストの省略設定
  "& .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  height: "100%",
  minHeight: "300px",
  display: "flex",
  flexDirection: "column",
}));

// データがない場合に表示するカスタムコンポーネント
const NoRowsOverlay = (): JSX.Element => {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        color: "rgba(0, 0, 0, 0.87)",
        minHeight: "150px",
      }}
    >
      検索履歴がありません
    </div>
  );
};

const getRealEstateTypeLabel = (type: string): string => {
  const types: Record<string, string> = {
    land: "土地",
    building: "建物",
    kubun_building: "区分建物",
  };
  return types[type] || type;
};

export const SearchHistoryGrid: React.FC<SearchHistoryGridProps> = ({
  loading,
  rows,
  onRowClick,
  pageSize,
  rowsPerPageOptions,
  rowCount,
  onPageSizeChange,
  page = 0,
  onPageChange,
}) => {
  // ページ変更ハンドラー
  const handlePageChange = (newPage: number): void => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  // ページサイズ変更ハンドラー
  const handlePageSizeChange = (newPageSize: number): void => {
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "createdAt",
      headerName: "検索日時",
      width: 160,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        return format(new Date(params.value), "yyyy/MM/dd HH:mm", {
          locale: ja,
        });
      },
    },
    {
      field: "prefectures",
      headerName: "都道府県",
      width: 150,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<number[]>) => {
        if (!params.value || !Array.isArray(params.value)) return "";
        return getPrefectureNamesByIds(params.value).join(", ");
      },
    },
    {
      field: "cityNames",
      headerName: "市区町村",
      width: 150,
      flex: 1.5,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string[]>) => {
        if (!params.value || !Array.isArray(params.value)) return "";
        return params.value.join(", ");
      },
    },
    {
      field: "ooaza",
      headerName: "大字・町丁目",
      width: 150,
      flex: 1.5,
      align: "center",
      headerAlign: "center",
      valueFormatter: (
        params: GridValueFormatterParams<string | undefined>
      ) => {
        return params.value;
      },
    },
    {
      field: "realEstateType",
      headerName: "種別",
      width: 100,
      flex: 0.7,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        return getRealEstateTypeLabel(params.value);
      },
    },
  ];

  return (
    <StyledDataGrid
      loading={loading}
      rows={rows}
      columns={columns}
      onRowClick={(
        params: GridRowParams<UserSearchHistoryTitleResponseType>
      ) => {
        onRowClick(params.row);
      }}
      pageSize={pageSize}
      rowsPerPageOptions={rowsPerPageOptions}
      rowCount={rowCount}
      pagination
      page={page}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      disableColumnMenu
      disableSelectionOnClick
      getRowId={(row: UserSearchHistoryTitleResponseType) => row.id}
      components={{
        NoRowsOverlay,
      }}
      autoHeight={false}
    />
  );
};
